/*
  eg-personalizer: product-page personalization (names or a logo, one ink or full color) for cases.

  Config comes from the product metafield custom.personalizer, written by Basecamp (/personalizer):
  the lid frame in mm and its printable areas (centre panel, side channels), a top-view photo per
  case color with the frame marked on it, layouts with fields already placed in mm (rotation,
  alignment, letter spacing), tier prices + fee variants, fonts and inks. The preview uses the same
  geometry as the print export (cap height = field height, shrink to fit, then align and rotate),
  so what the customer sees is what prints.

  Layouts can be flipped (mirrored left-right: left channel <-> right, direction reversed) and take
  an optional accent: small fixed-font text, e.g. at the top of a side channel, offered only where it
  clears the layout. The same rules run in Basecamp (_personalizer/core.ts) when the design is saved.

  Add to cart: the design is saved once in Basecamp (personalizer-public), which returns one id per
  case. Each case becomes its own cart line with visible properties (Name, …) and hidden _pz_design /
  _pz_group; the upcharge is a line of the hidden fee product (quantity = cases) with the same
  _pz_group. eg-personalizer-cart.js keeps the two in step on the cart page.
*/
(function () {
  'use strict';

  var MAX_LOGO_PX = 2400;
  var MIN_DPI = 150;
  var BAKE_DPI = 400;
  var SNAP_MM = 1.5;
  var PROXY_PX = 720; // the logo on screen; the full file is only used for the print
  var INK_FILL = { white: '#f4f2ec', black: '#141414', clear: 'rgba(255,255,255,.24)' };
  var SVGNS = 'http://www.w3.org/2000/svg';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(cents, fmt) {
    var v = (cents / 100).toFixed(2);
    return (fmt || '${{amount}}').replace(/\{\{\s*amount[a-z_]*\s*\}\}/, v.replace(/\.00$/, ''));
  }
  // Save and cart calls give up after 30 seconds, so nobody sits on "Adding…"
  function timedFetch(url, opts) {
    var c = window.AbortController ? new AbortController() : null, t = c && setTimeout(function () { c.abort(); }, 30000);
    if (c) opts.signal = c.signal;
    return fetch(url, opts).then(function (r) { clearTimeout(t); return r; }, function (e) {
      clearTimeout(t);
      throw new Error(e && e.name === 'AbortError' ? 'That took too long. Please check your connection and try again.' : 'save');
    });
  }

  // The logo file travels from the product page to the studio in the browser (too big for sessionStorage).
  function pzDb() {
    return new Promise(function (res, rej) {
      var r = indexedDB.open('egpz', 1);
      r.onupgradeneeded = function () { r.result.createObjectStore('f'); };
      r.onsuccess = function () { res(r.result); };
      r.onerror = function () { rej(r.error); };
    });
  }
  function el(tag, attrs, html) {
    var n = document.createElement(tag);
    if (attrs) for (var k in attrs) if (attrs[k] != null) n.setAttribute(k, attrs[k]);
    if (html != null) n.innerHTML = html;
    return n;
  }

  // ── Fonts + text fitting (mirrors Basecamp's export) ─────────────────────────────────────────
  var fontLoads = {};
  function loadFont(f) {
    if (!f || fontLoads[f.id]) return fontLoads[f && f.id] || Promise.resolve();
    fontLoads[f.id] = new FontFace(f.family, 'url(' + f.woff_url + ')').load().then(function (ff) {
      document.fonts.add(ff);
    }).catch(function () {});
    return fontLoads[f.id];
  }
  var measure = document.createElement('canvas').getContext('2d');
  var capCache = {};
  // Size + position of one line in its field, field-local (x along the text, origin at the centre).
  function fitText(family, text, fld) {
    measure.font = '100px "' + family + '"';
    var cap = capCache[family];
    if (!cap) {
      cap = (measure.measureText('H').actualBoundingBoxAscent || 70) / 100;
      if (document.fonts.check('100px "' + family + '"')) capCache[family] = cap;
    }
    var tr = fld.tracking || 0, gaps = Math.max(0, Array.from(text).length - 1);
    var size = fld.h_mm / cap;
    var width = (measure.measureText(text).width / 100 + tr * gaps) * size;
    if (width > fld.w_mm) { size *= fld.w_mm / width; width = fld.w_mm; }
    var x0 = fld.align === 'start' ? -fld.w_mm / 2 : fld.align === 'end' ? fld.w_mm / 2 - width : -width / 2;
    return { size: size, x0: x0, baseline: cap * size / 2, spacing: tr * size };
  }
  // ── Flip + accent geometry (mirrors core.ts) ──
  var MIRROR_AREA = { left: 'right', right: 'left' };
  function mirror(els) {
    return els.map(function (e) {
      return Object.assign({}, e, {
        area: MIRROR_AREA[e.area] || e.area, x_mm: -e.x_mm, rot: e.rot ? -e.rot : 0,
        align: e.align === 'start' ? 'end' : e.align === 'end' ? 'start' : 'center'
      });
    });
  }
  function isFlippable(els) { return els.some(function (e) { return e.x_mm !== 0 || e.rot || (e.align && e.align !== 'center'); }); }
  function boxOf(e) {
    var across = e.kind === 'text' ? e.h_mm * 1.5 : e.h_mm, w = e.rot ? across : e.w_mm, h = e.rot ? e.w_mm : across;
    return { x1: e.x_mm - w / 2, x2: e.x_mm + w / 2, y1: e.y_mm - h / 2, y2: e.y_mm + h / 2 };
  }
  function accentFits(layout, accent) {
    return accent.every(function (a) {
      return layout.every(function (l) {
        var A = boxOf(a), L = boxOf(l);
        return A.x2 + 1 <= L.x1 || L.x2 + 1 <= A.x1 || A.y2 + 1 <= L.y1 || L.y2 + 1 <= A.y1;
      });
    });
  }
  function logoFit(fld, w, h) {
    var ar = w / h, fw = Math.min(fld.w_mm, fld.h_mm * ar);
    return { w: fw, h: fw / ar, dpi: Math.round(w / (fw / 25.4)) };
  }
  // The customer's edits: crop (fractions off each edge of the image), then size, rotation and offset
  // inside the logo's space. Everything outside the space is trimmed.
  function freshEdit() { return { x: 0, y: 0, s: 1, r: 0, crop: { l: 0, t: 0, r: 0, b: 0 } }; }
  function logoPlace(fld, L, e) {
    var cf = 1 - e.crop.l - e.crop.r, ch = 1 - e.crop.t - e.crop.b;
    var fit = logoFit(fld, L.w * cf, L.h * ch), w = fit.w * e.s, h = fit.h * e.s, sw = w / cf, sh = h / ch;
    return { w: w, h: h, sw: sw, sh: sh, ix: -w / 2 - e.crop.l * sw, iy: -h / 2 - e.crop.t * sh, dpi: Math.round(L.w / (sw / 25.4)) };
  }
  // Bake the edits into a PNG the exact shape of the logo's space, so the print needs no clip mask and
  // matches the preview. Baked at the logo's own resolution, so Basecamp still sees a soft logo as soft.
  function bakedLogo(L, fld, e) {
    var p = logoPlace(fld, L, e), dpi = Math.max(100, Math.min(BAKE_DPI, p.dpi)), mm = dpi / 25.4;
    return new Promise(function (resolve) {
      var img = new Image();
      img.onload = function () {
        var c = document.createElement('canvas'); c.width = Math.round(fld.w_mm * mm); c.height = Math.round(fld.h_mm * mm);
        var g = c.getContext('2d');
        g.imageSmoothingQuality = 'high';
        g.translate(c.width / 2 + e.x * mm, c.height / 2 + e.y * mm); g.rotate(e.r * Math.PI / 180);
        g.beginPath(); g.rect(-p.w / 2 * mm, -p.h / 2 * mm, p.w * mm, p.h * mm); g.clip();
        g.drawImage(img, p.ix * mm, p.iy * mm, p.sw * mm, p.sh * mm);
        resolve({ dataUrl: c.toDataURL('image/png'), w: c.width, h: c.height });
      };
      img.src = L.dataUrl;
    });
  }

  // ── Logo processing (in the browser; only the finished PNG is uploaded) ──────────────────────
  function readImage(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('read')); };
      img.src = url;
    });
  }
  /*
    mode "mono": a mask of where the single ink goes, black on transparent. Transparent logos use
    their alpha; flat logos use dark-on-light (or light-on-dark with invert).
    mode "color": the art as-is, optionally with a white background knocked out.
  */
  function processLogo(img, mode, opts) {
    var w = img.naturalWidth || img.width || 1200, h = img.naturalHeight || img.height || 1200;
    var s = Math.min(1, MAX_LOGO_PX / Math.max(w, h));
    if (img.src.indexOf('image/svg') > -1 || /\.svg/i.test(opts.name || '')) s = MAX_LOGO_PX / Math.max(w, h);
    w = Math.max(1, Math.round(w * s)); h = Math.max(1, Math.round(h * s));
    var c = document.createElement('canvas'); c.width = w; c.height = h;
    var g = c.getContext('2d');
    g.drawImage(img, 0, 0, w, h);
    var d = g.getImageData(0, 0, w, h), p = d.data, n = w * h, i;
    var see = 0;
    for (i = 0; i < n; i++) if (p[i * 4 + 3] < 250) see++;
    var transparent = see / n > 0.02;
    if (mode === 'mono') {
      // One ink: the darker parts of the logo (seen over white) print; "invert" prints the lighter
      // parts instead. A logo that's all light (e.g. white on transparent) falls back to its shape.
      var mask = new Uint8Array(n), opaque = 0, on = 0;
      for (i = 0; i < n; i++) {
        var o = i * 4, a = p[o + 3] / 255;
        var lum = (0.299 * p[o] + 0.587 * p[o + 1] + 0.114 * p[o + 2]) * a + 255 * (1 - a);
        if (a > 0.5) opaque++;
        var m = opts.invert ? a > 0.5 && lum > 150 : lum < 150;
        if (m) { mask[i] = 1; on++; }
      }
      var useAlpha = transparent && on < opaque * 0.005;
      for (i = 0; i < n; i++) {
        var q4 = i * 4, show = useAlpha ? p[q4 + 3] > 128 : mask[i];
        p[q4] = p[q4 + 1] = p[q4 + 2] = 0; p[q4 + 3] = show ? 255 : 0;
      }
    } else if (opts.knockout && !transparent) {
      for (i = 0; i < n; i++) {
        var q = i * 4;
        if (p[q] > 238 && p[q + 1] > 238 && p[q + 2] > 238) p[q + 3] = 0;
      }
    }
    g.putImageData(d, 0, 0);
    // trim empty edges so the logo fills its field
    var minX = w, minY = h, maxX = -1, maxY = -1;
    for (var y = 0; y < h; y++) for (var x = 0; x < w; x++) if (p[(y * w + x) * 4 + 3] > 10) {
      if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    if (maxX < 0) return null;
    var tw = maxX - minX + 1, th = maxY - minY + 1;
    var t = document.createElement('canvas'); t.width = tw; t.height = th;
    t.getContext('2d').drawImage(c, minX, minY, tw, th, 0, 0, tw, th);
    var ps = Math.min(1, PROXY_PX / Math.max(tw, th)), small = document.createElement('canvas');
    small.width = Math.max(1, Math.round(tw * ps)); small.height = Math.max(1, Math.round(th * ps));
    var sg = small.getContext('2d'); sg.imageSmoothingQuality = 'high'; sg.drawImage(t, 0, 0, small.width, small.height);
    return { dataUrl: t.toDataURL('image/png'), w: tw, h: th, transparent: transparent, small: small, tints: {} };
  }
  // The on-screen copy of a logo: small, and already in the ink color, so the preview needs no mask.
  function proxyLogo(L, fill) {
    var key = fill || 'color';
    if (L.tints[key]) return L.tints[key];
    var c = document.createElement('canvas'); c.width = L.small.width; c.height = L.small.height;
    var g = c.getContext('2d'); g.drawImage(L.small, 0, 0);
    if (fill) { g.globalCompositeOperation = 'source-in'; g.fillStyle = fill; g.fillRect(0, 0, c.width, c.height); }
    return (L.tints[key] = c.toDataURL('image/png'));
  }

  // ── Markup ───────────────────────────────────────────────────────────────────────────────────
  // Built here from the config so the block works on any product template (snippets/
  // eg-personalizer-boot.liquid only outputs the data) and in Basecamp's preview.
  var ICON = {
    pen: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>',
    chev: '<svg class="egpz-teaser__chev" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M3 6l5 5 5-5"/></svg>',
    plus: '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M8 3v10M3 8h10"/></svg>',
    up: '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M8 11V2M4.5 5.5 8 2l3.5 3.5M2 11v3h12v-3"/></svg>',
    expand: '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M12 3h5v5M8 17H3v-5M17 3l-6 6M3 17l6-6"/></svg>',
    flip: '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M10 2v16"/><path d="M7 5 2.5 14H7z"/><path d="M13 5l4.5 9H13z"/></svg>'
  };
  function markup(id, cfg, fromText, studioUrl) {
    var notes = cfg.notes || {};
    var turnaround = notes.turnaround || 'Printed in our shop. Ships in 3–5 business days.';
    return '' +
      '<button type="button" class="egpz-teaser" data-pz-toggle aria-expanded="false" aria-controls="egpz-panel-' + id + '">' +
        '<span class="egpz-teaser__art" aria-hidden="true"><span class="egpz-teaser__zoom" data-pz-tzoom><img data-pz-tphoto alt="" draggable="false">' +
          '<svg class="egpz-stage__svg" data-pz-tsvg preserveAspectRatio="xMidYMid meet"></svg></span></span>' +
        '<span class="egpz-teaser__body">' +
          '<span class="egpz-teaser__tag">' + ICON.pen + 'Personalize it</span>' +
          '<strong class="egpz-teaser__title">Make it yours</strong>' +
          '<span class="egpz-teaser__sub">A name, initials or your logo, printed on the lid.</span>' +
          '<span class="egpz-teaser__meta"><b>' + esc(fromText) + '</b><span>Great as a gift</span></span>' +
        '</span>' +
        '<span class="egpz-teaser__cta"><span data-pz-tcta>Personalize</span>' + ICON.chev + '</span>' +
      '</button>' +
      (studioUrl ? '<a class="egpz-studio" href="' + esc(studioUrl) + '" data-pz-handoff>Topo maps, monograms and more designs in the <b>Case Studio</b> →</a>' : '') +
      '<div class="egpz-panel" id="egpz-panel-' + id + '" data-pz-panel hidden>' +
        '<div class="egpz-stage" data-pz-stage>' +
          '<div class="egpz-stage__img"><div class="egpz-stage__zoom" data-pz-zoom>' +
            '<img data-pz-photo alt="" width="900" height="900" draggable="false">' +
            '<svg class="egpz-stage__svg" data-pz-svg aria-hidden="true" preserveAspectRatio="xMidYMid meet"></svg>' +
          '</div></div>' +
          '<div class="egpz-stage__bar"><p class="egpz-stage__cap" data-pz-cap aria-live="polite"></p>' +
            '<button type="button" class="egpz-flip" data-pz-flip aria-pressed="false" hidden>' + ICON.flip + 'Flip</button>' +
            (studioUrl ? '<a class="egpz-flip egpz-expand" href="' + esc(studioUrl) + '" data-pz-handoff>' + ICON.expand + 'More designs &amp; full editor</a>' : '') + '</div>' +
        '</div>' +
        '<div class="egpz-controls">' +
          '<div class="egpz-group egpz-step"><p class="egpz-label" id="egpz-l-kind-' + id + '">What to print</p>' +
            '<div class="egpz-kinds" data-pz-kinds role="group" aria-labelledby="egpz-l-kind-' + id + '"></div></div>' +
          '<div class="egpz-group egpz-step"><p class="egpz-label" id="egpz-l-lay-' + id + '">Layout</p>' +
            '<div class="egpz-layouts" data-pz-layouts role="group" aria-labelledby="egpz-l-lay-' + id + '"></div></div>' +
          '<div class="egpz-group egpz-step" data-pz-logo hidden><label class="egpz-label" for="egpz-file-' + id + '">Logo</label>' +
            '<label class="egpz-upload"><input type="file" id="egpz-file-' + id + '" accept="image/png,image/jpeg,image/svg+xml,image/webp" data-pz-file>' +
            '<span>' + ICON.up + '<span data-pz-file-label>Upload your logo</span></span></label>' +
            '<p class="egpz-note" data-pz-logo-note></p><div class="egpz-logo-opts" data-pz-logo-opts></div></div>' +
          '<div class="egpz-group egpz-step"><p class="egpz-label" data-pz-cases-label>Your text</p>' +
            '<div class="egpz-cases" data-pz-cases></div>' +
            '<button type="button" class="egpz-add" data-pz-add>' + ICON.plus + 'Add another case</button>' +
            '<div class="egpz-ladder" data-pz-ladder aria-live="polite" hidden></div>' +
            '<p class="egpz-note" data-pz-quote hidden>Need more than ' + esc(cfg.max_cases) + '? <a href="' + esc(cfg.quote_url || '/pages/corporate-gifts') + '">Get a volume quote</a>.</p></div>' +
          '<div class="egpz-group egpz-step"><p class="egpz-label">Style</p>' +
            '<div class="egpz-chips" data-pz-font-wrap><div class="egpz-chips" data-pz-fonts role="group" aria-label="Font"></div></div>' +
            '<div class="egpz-chips" data-pz-ink-wrap><div class="egpz-chips" data-pz-inks role="group" aria-label="Print color"></div></div>' +
            '<div class="egpz-extra" data-pz-accent-wrap hidden>' +
              '<button type="button" class="egpz-extra__toggle" data-pz-accent-toggle aria-expanded="false">+ Add a corner accent <span>a number, initials or a year</span></button>' +
              '<p class="egpz-extra__label" data-pz-accent-label hidden>Corner accent <span>small sideways text, typed in Your text above</span></p>' +
              '<div class="egpz-layouts" data-pz-accents role="group" aria-label="Accent corner" hidden></div></div></div>' +
          '<p class="egpz-note egpz-note--warn" data-pz-unavail hidden></p>' +
          '<div class="egpz-sum" data-pz-sum aria-live="polite"></div>' +
          '<label class="egpz-approve" data-pz-approve-box><input type="checkbox" data-pz-approve><span><b>I’ve checked my proof.</b> Spelling, placement and colors are how I want them. Printed colors and position can vary slightly from the screen. Custom prints are final sale: no returns or refunds unless it arrives damaged or we made a mistake.</span></label>' +
          '<p class="egpz-error" data-pz-error role="alert" hidden></p>' +
          '<button type="button" class="egpz-cta" data-pz-cta>Add to cart</button>' +
          '<ul class="egpz-trust"><li>' + esc(turnaround) + '</li><li>We can refuse any design, including hateful, explicit or trademarked content, or anything you don’t have rights to use. If we do, we’ll refund you.</li>' +
            (notes.final_sale ? '<li>' + esc(notes.final_sale) + '</li>' : '') + '</ul>' +
        '</div>' +
      '</div>';
  }

  // Where the block goes: right above the product form's quantity + Add to Cart row (after the
  // color, insert and bulk-pricing pickers), else a template's mount point, else where it was rendered.
  function place(root) {
    var form = document.querySelector('form[action*="/cart/add"] .product__submit__holder');
    var mount = document.querySelector('[data-eg-pz-mount]');
    if (form) form.parentNode.insertBefore(root, form);
    else if (mount) mount.appendChild(root);
    // inside the product form: Enter in our fields must never submit it (that would add a plain case)
    root.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.tagName === 'INPUT') { e.preventDefault(); e.target.blur(); }
    });
  }

  // ── The block ────────────────────────────────────────────────────────────────────────────────
  // Steps: Design (Name | Logo) → Placement → Logo → Names → Style (font, print color) → an optional
  // corner accent. Full color is a print color on logo designs (charged as tier 3). The case color
  // is the one picked in the product form above; there's no second color picker here.
  function init(root) {
    var cfg, product;
    try {
      cfg = JSON.parse(root.querySelector('[data-pz-config]').textContent);
      product = JSON.parse(root.querySelector('[data-pz-product]').textContent);
    } catch (e) { return; }
    if (!cfg || cfg.enabled === false || !cfg.templates || !cfg.templates.length) return;
    // "Preview only" publishes from Basecamp show the block just for ?pz=preview links (and in
    // Basecamp's own preview, which sets window.EGPZ_PREVIEW).
    var bcPreview = !!window.EGPZ_PREVIEW;
    if (cfg.preview && !bcPreview && !/[?&]pz=preview\b/.test(location.search)) return;

    var fees = {};
    cfg.tiers.forEach(function (t) { fees[t.tier] = t; });
    var cheapest = cfg.tiers.reduce(function (m, t) { return !m || t.price < m.price ? t : m; }, null);
    // The full-page Case Studio, when this product is one of its cases (keeps ?pz=preview while previewing).
    var studioUrl = (cfg.cases || []).some(function (c) { return c.handle === product.handle; })
      ? '/pages/customize?case=' + product.handle : null;
    var dataNodes = [].slice.call(root.querySelectorAll('script[type="application/json"]'));
    root.innerHTML = markup(root.id || 'egpz', cfg, 'from +' + money(cheapest.price * 100, product.money), bcPreview ? null : studioUrl);
    dataNodes.forEach(function (n) { root.appendChild(n); });
    place(root);
    root.hidden = false;
    // Text = tier 1 layouts; Logo / Logo + text = tier 2 layouts without / with a text field (same price).
    function hasText(t) { return t.fields.some(function (f) { return f.kind === 'text'; }); }
    var KINDS = [
      { id: 'text', label: 'Text', tier: 1 },
      { id: 'logo', label: 'Logo', tier: 2, text: false },
      { id: 'logo-text', label: 'Logo + text', tier: 2, text: true }
    ].map(function (k) {
      k.tpls = cfg.templates.filter(function (t) { return t.slug !== 'logo-full' && t.tier === k.tier && (k.tier === 1 || hasText(t) === k.text); });
      return k;
    }).filter(function (k) { return k.tpls.length && (k.tier === 1 ? fees[1] : (fees[2] || fees[3])); });
    if (!KINDS.length) return;

    var colorOpt = product.options.findIndex(function (o) { return /colou?r/i.test(o); });
    function currentVariant() { return product.variants.find(function (v) { return v.id === product.current; }) || product.variants[0]; }
    // The case color comes from the product's own picker.
    function color() {
      var v = currentVariant();
      if (colorOpt < 0) return cfg.colors[0];
      var name = String(v.options[colorOpt]).toLowerCase();
      return cfg.colors.find(function (c) { return c.name.toLowerCase() === name; }) || null;
    }
    var shown = cfg.colors[0]; // last configured color, so the preview never goes blank

    var st = {
      open: false,
      kind: KINDS[0].id,
      tpl: null,
      font: cfg.fonts[0] && cfg.fonts[0].id,
      ink: null,
      flip: false,
      accent: null,
      accentOpen: false,
      cases: [{}],
      openIdx: 0,
      logo: null, // {img, name, print:{dataUrl,w,h,transparent}, invert, knockout, size, edit}
      busy: false,
      approved: false,
      error: ''
    };
    function kind() { return KINDS.find(function (k) { return k.id === st.kind; }) || KINDS[0]; }
    function tplsForKind() { return kind().tpls; }
    function tpl() { return cfg.templates.find(function (x) { return x.slug === st.tpl; }); }
    function font(id) { var want = id || st.font; return cfg.fonts.find(function (f) { return f.id === want; }) || cfg.fonts[0]; }
    function inkOf(id) { return cfg.inks.find(function (i) { return i.id === id; }) || { id: id, label: id, swatch: '#ccc' }; }
    function tier() { return kind().tier === 1 ? 1 : st.ink === 'color' ? 3 : 2; }
    function ink() { return st.ink; }
    function inkChoices() {
      var logo = kind().tier === 2, c = shown, list = (c && c.inks || []).filter(function (id) { return id !== 'color'; });
      if (logo && fees[3]) list = ['color'].concat(list);
      if (logo && !fees[2]) list = ['color'];
      return list;
    }
    function accentOf(slug) { return (cfg.accents || []).find(function (a) { return a.slug === slug; }); }
    // a layout's placed fields, flipped when the customer flipped it, logo grown for fill / oversize
    function layoutFields(t) {
      var els = st.flip && isFlippable(t.fields) ? mirror(t.fields) : t.fields;
      return st.logo && st.logo.size && st.logo.size !== 'fit' ? growLogo(els) : els;
    }
    // Fill / oversize (same rule as core.ts applyLogoSize): a logo alone in its area takes the whole
    // area, the dotted outline, in its own frame; one sharing the area with text keeps its box.
    function growLogo(els) {
      return els.map(function (e) {
        if (e.kind !== 'logo' || els.some(function (o) { return o !== e && o.area === e.area; })) return e;
        var a = (cfg.areas || []).find(function (x) { return x.id === e.area; });
        if (!a) return e;
        return Object.assign({}, e, { x_mm: a.x_mm, y_mm: a.y_mm, w_mm: e.rot ? a.h_mm : a.w_mm, h_mm: e.rot ? a.w_mm : a.h_mm });
      });
    }
    function fittingAccents() { var t = tpl(); return t ? (cfg.accents || []).filter(function (a) { return accentFits(layoutFields(t), a.fields); }) : []; }
    // everything printed on the lid: layout + accent
    function fields() {
      var t = tpl(); if (!t) return [];
      var a = st.accent && accentOf(st.accent);
      return a ? layoutFields(t).concat(a.fields) : layoutFields(t);
    }
    function textFields() {
      var seen = {};
      return fields().filter(function (f) { if (f.kind !== 'text' || seen[f.key]) return false; seen[f.key] = 1; return true; });
    }
    function logoField() { return fields().find(function (f) { return f.kind === 'logo'; }) || null; }
    function needsFont() { return textFields().some(function (f) { return !f.font; }); }

    function syncDefaults() {
      var c = color();
      if (c) shown = c;
      if (!tplsForKind().some(function (t) { return t.slug === st.tpl; })) {
        st.tpl = tplsForKind()[0].slug;
        if (tpl().font) st.font = tpl().font;
      }
      var inks = inkChoices();
      if (inks.indexOf(st.ink) < 0) st.ink = inks[0];
      if (st.accent && !fittingAccents().some(function (a) { return a.slug === st.accent; })) st.accent = null;
      if (st.logo) reprocess();
    }

    var q = function (s) { return root.querySelector(s); };
    var ui = {
      toggle: q('[data-pz-toggle]'), panel: q('[data-pz-panel]'), stage: q('[data-pz-stage]'),
      photo: q('[data-pz-photo]'), zoom: q('[data-pz-zoom]'), svg: q('[data-pz-svg]'), cap: q('[data-pz-cap]'),
      flip: q('[data-pz-flip]'), kinds: q('[data-pz-kinds]'), layouts: q('[data-pz-layouts]'),
      logoWrap: q('[data-pz-logo]'), file: q('[data-pz-file]'), fileLabel: q('[data-pz-file-label]'),
      logoNote: q('[data-pz-logo-note]'), logoOpts: q('[data-pz-logo-opts]'),
      casesLabel: q('[data-pz-cases-label]'), cases: q('[data-pz-cases]'), add: q('[data-pz-add]'), quote: q('[data-pz-quote]'),
      fontWrap: q('[data-pz-font-wrap]'), fonts: q('[data-pz-fonts]'), inkWrap: q('[data-pz-ink-wrap]'), inks: q('[data-pz-inks]'),
      accentWrap: q('[data-pz-accent-wrap]'), accentToggle: q('[data-pz-accent-toggle]'), accentLabel: q('[data-pz-accent-label]'), accents: q('[data-pz-accents]'),
      unavail: q('[data-pz-unavail]'), sum: q('[data-pz-sum]'), cta: q('[data-pz-cta]'), err: q('[data-pz-error]'),
      ladder: q('[data-pz-ladder]'),
      tphoto: q('[data-pz-tphoto]'), tzoom: q('[data-pz-tzoom]'), tsvg: q('[data-pz-tsvg]'), tcta: q('[data-pz-tcta]')
    };

    // ── preview ──
    function areaRect(a, attrs) {
      return '<rect x="' + (a.x_mm - a.w_mm / 2) + '" y="' + (a.y_mm - a.h_mm / 2) + '" width="' + a.w_mm + '" height="' + a.h_mm + '" rx="1.5" ' + attrs + '/>';
    }
    function drawPreview(svg, c, flds, values, opts) {
      var zw = cfg.zone.w_mm, zh = cfg.zone.h_mm;
      svg.setAttribute('viewBox', (-zw / 2) + ' ' + (-zh / 2) + ' ' + zw + ' ' + zh);
      var f = font(opts.font), fam = f ? f.family : 'sans-serif';
      function famOf(fld) { var ff = fld.font && cfg.fonts.find(function (x) { return x.id === fld.font; }); return ff ? ff.family : fam; }
      var curInk = ink();
      var textInk = curInk === 'color' ? (c.color_ink || 'white') : curInk;
      var fill = INK_FILL[textInk] || '#f4f2ec';
      var used = {}; flds.forEach(function (fld) { used[fld.area] = 1; });
      var out = '';
      // Printable areas: thumbnails show the lid map, the main preview outlines the areas in use.
      (cfg.areas || []).forEach(function (a) {
        if (opts.small) out += areaRect(a, 'fill="#fff" fill-opacity="' + (used[a.id] ? '.1' : '.04') + '"');
        else if (used[a.id] && !opts.clean) out += areaRect(a, 'fill="none" stroke="' + fill + '" stroke-opacity=".3" stroke-width=".35" stroke-dasharray="1.6 1.2" vector-effect="non-scaling-stroke"');
      });
      flds.forEach(function (fld, i) {
        var turn = '<g transform="translate(' + fld.x_mm + ' ' + fld.y_mm + ')' + (fld.rot ? ' rotate(' + fld.rot + ')' : '') + '">';
        if (fld.kind === 'logo') {
          var L = st.logo && st.logo.print;
          if (!L) {
            out += turn + '<rect x="' + (-fld.w_mm / 2) + '" y="' + (-fld.h_mm / 2) + '" width="' + fld.w_mm + '" height="' + fld.h_mm +
              '" fill="none" stroke="' + fill + '" stroke-opacity=".55" stroke-width="' + zw / 250 + '" stroke-dasharray="' + zw / 80 + ' ' + zw / 120 + '"/>' +
              '<text x="0" y="0" text-anchor="middle" dominant-baseline="middle" font-family="sans-serif" font-size="' +
              Math.min(fld.h_mm, fld.w_mm) / 5 + '" fill="' + fill + '" fill-opacity=".7">LOGO</text></g>';
            return;
          }
          // drawn in the field's own frame so a logo turned up a channel rotates about its centre;
          // anything past the field's box or the crop is clipped, as it is in the uploaded file
          var edit = opts.small ? freshEdit() : st.logo.edit, p = logoPlace(fld, L, edit), cid = 'pzc' + opts.id + i, crop = 'pzk' + opts.id + i;
          if (!opts.small) st.logo.drawnS = edit.s;
          var href = 'href="' + proxyLogo(L, curInk === 'color' ? null : INK_FILL[curInk] || fill) + '" x="' + p.ix + '" y="' + p.iy + '" width="' + p.sw + '" height="' + p.sh + '" preserveAspectRatio="none"';
          out += turn + '<clipPath id="' + cid + '"><rect x="' + (-fld.w_mm / 2) + '" y="' + (-fld.h_mm / 2) + '" width="' + fld.w_mm + '" height="' + fld.h_mm + '"/></clipPath>' +
            '<g clip-path="url(#' + cid + ')"><g data-pz-logo-g transform="translate(' + edit.x + ' ' + edit.y + ') rotate(' + edit.r + ')">' +
            '<clipPath id="' + crop + '"><rect x="' + (-p.w / 2) + '" y="' + (-p.h / 2) + '" width="' + p.w + '" height="' + p.h + '"/></clipPath><g clip-path="url(#' + crop + ')"><image ' + href + '/></g>';
          out += '</g></g>' + (opts.small ? '' : '<rect class="egpz-grab" data-pz-grab x="' + (-fld.w_mm / 2) + '" y="' + (-fld.h_mm / 2) + '" width="' + fld.w_mm + '" height="' + fld.h_mm + '" fill="transparent"/>') + '</g>';
          return;
        }
        var typed = (values[fld.key] || '').trim();
        var v = typed || fld.placeholder || (opts.small ? fld.label : '');
        if (fld.uppercase) v = v.toUpperCase();
        if (!v) return;
        var t = fitText(famOf(fld), v, fld);
        var ghost = !typed && !opts.small;
        out += turn + '<text x="' + t.x0 + '" y="' + t.baseline + '" font-family="&quot;' + esc(famOf(fld)) + '&quot;" font-size="' + t.size + '"' +
          (t.spacing ? ' letter-spacing="' + t.spacing + '"' : '') + ' fill="' + fill + '"' + (ghost ? ' fill-opacity=".45"' : '') +
          (textInk === 'clear' ? ' stroke="rgba(255,255,255,.4)" stroke-width="' + t.size / 70 + '"' : '') + '>' + esc(v) + '</text></g>';
      });
      svg.innerHTML = out;
    }
    function renderPreview() {
      var c = shown, t = tpl();
      if (ui.photo.getAttribute('data-src') !== c.photo_url) {
        ui.photo.src = c.photo_url + (c.photo_url.indexOf('?') > -1 ? '&' : '?') + 'width=900';
        ui.photo.setAttribute('data-src', c.photo_url);
        ui.photo.alt = c.name + ' ' + cfg.name + ', top view';
      }
      var b = c.box, s = ui.svg.style;
      s.left = b.x * 100 + '%'; s.top = b.y * 100 + '%'; s.width = b.w * 100 + '%'; s.height = b.h * 100 + '%';
      // Zoom the square photo so the lid frame spans ~62% of the 3:2 window (the whole case shows), centred.
      var k = Math.max(1, Math.min(0.62 / b.w, 0.5 / b.h));
      ui.zoom.style.transform = 'translate(' + (0.5 - (b.x + b.w / 2) * k) * 100 + '%,' + (1 / 3 - (b.y + b.h / 2) * k) * 100 + '%) scale(' + k + ')';
      drawPreview(ui.svg, c, fields(), st.cases[st.openIdx] || {}, { id: 'main' });
      var canFlip = isFlippable(t.fields);
      ui.flip.hidden = !canFlip;
      ui.flip.setAttribute('aria-pressed', canFlip && st.flip ? 'true' : 'false');
      var first = textFields()[0], name = first ? (st.cases[st.openIdx] || {})[first.key] : '';
      ui.cap.textContent = (st.cases.length > 1 ? 'Case ' + (st.openIdx + 1) + ' of ' + st.cases.length : c.name + ' case') + (name ? ' · ' + name.toUpperCase() : '');
    }

    // ── controls ──
    function chip(attrs, inner, pressed) {
      var a = Object.assign({ type: 'button', 'aria-pressed': pressed ? 'true' : 'false' }, attrs);
      a.class = 'egpz-chip' + (attrs.class ? ' ' + attrs.class : '');
      return el('button', a, inner);
    }
    // a placement-style chip: lid-map thumbnail + name
    function thumbChip(attrs, name, pressed, flds, fontId) {
      var b = chip(Object.assign({ class: 'egpz-layout', 'aria-label': name }, attrs), '', pressed);
      var thumb = el('span', { class: 'egpz-layout__thumb' });
      var sv = document.createElementNS(SVGNS, 'svg');
      sv.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      sv.setAttribute('aria-hidden', 'true');
      thumb.appendChild(sv);
      b.appendChild(thumb);
      b.appendChild(el('span', null, esc(name)));
      drawPreview(sv, shown, flds, {}, { id: 'c' + (attrs['data-tpl'] || attrs['data-accent'] || 'none'), small: true, font: fontId });
      return b;
    }
    function price(t) { return '+' + money(t.price * 100, product.money); }
    function renderKinds() {
      ui.kinds.innerHTML = '';
      KINDS.forEach(function (k) {
        var p = fees[k.tier] || fees[3], from = k.tier === 2 && fees[2] && fees[3];
        ui.kinds.appendChild(chip({ 'data-kind': k.id, class: 'egpz-kind' }, '<span>' + k.label + '</span><small>' + (from ? 'from ' : '') + esc(price(p)) + '</small>', st.kind === k.id));
      });
    }
    function renderLayouts() {
      ui.layouts.innerHTML = '';
      tplsForKind().forEach(function (t) {
        ui.layouts.appendChild(thumbChip({ 'data-tpl': t.slug }, t.name, st.tpl === t.slug, layoutFields(t), t.font));
      });
      var on = ui.layouts.querySelector('[aria-pressed="true"]');
      if (on && ui.layouts.scrollWidth > ui.layouts.clientWidth) ui.layouts.scrollLeft = Math.max(0, on.offsetLeft - ui.layouts.clientWidth / 2 + on.offsetWidth / 2);
    }
    // Sliders and dragging move the logo without redrawing the (large) image.
    function resetPlace() { if (st.logo) { st.logo.edit.x = 0; st.logo.edit.y = 0; st.logo.edit.s = 1; } }
    function placeLogo() {
      var g = ui.svg.querySelector('[data-pz-logo-g]'), e = st.logo && st.logo.edit;
      if (g && e) g.setAttribute('transform', 'translate(' + e.x + ' ' + e.y + ') rotate(' + e.r + ') scale(' + e.s / st.logo.drawnS + ')');
    }
    // at most once a frame, however fast the finger moves
    var placing = 0;
    function queuePlace() { if (!placing) placing = requestAnimationFrame(function () { placing = 0; placeLogo(); }); }
    function clampEdit() {
      var lf = logoField(), e = st.logo && st.logo.edit;
      if (!lf || !e) return;
      // the logo's centre stays inside its space, and it snaps to centre
      var mx = lf.w_mm / 2, my = lf.h_mm / 2;
      e.x = Math.abs(e.x) < SNAP_MM ? 0 : Math.max(-mx, Math.min(mx, e.x));
      e.y = Math.abs(e.y) < SNAP_MM ? 0 : Math.max(-my, Math.min(my, e.y));
    }
    function cropper(L) {
      var c = L.edit.crop, box = el('div', { class: 'egpz-crop', style: 'aspect-ratio:' + L.print.w + '/' + L.print.h + ';width:' + Math.round(Math.min(300, 240 * L.print.w / L.print.h)) + 'px' }, '<img alt="" draggable="false" src="' + L.print.dataUrl + '">');
      var rect = el('div', { class: 'egpz-crop__box' }, '<i data-c="tl"></i><i data-c="tr"></i><i data-c="bl"></i><i data-c="br"></i>');
      function paint() { rect.style.left = c.l * 100 + '%'; rect.style.top = c.t * 100 + '%'; rect.style.right = c.r * 100 + '%'; rect.style.bottom = c.b * 100 + '%'; }
      box.appendChild(rect); paint();
      box.addEventListener('pointerdown', function (e) {
        var c0 = e.target.getAttribute('data-c');
        if (!c0 && e.target !== rect) return;
        e.preventDefault(); box.setPointerCapture(e.pointerId);
        var r = box.getBoundingClientRect(), sx = e.clientX, sy = e.clientY, o = Object.assign({}, c), MIN = 0.08;
        function move(ev) {
          var dx = (ev.clientX - sx) / r.width, dy = (ev.clientY - sy) / r.height;
          if (!c0) { // drag the whole window
            dx = Math.max(-o.l, Math.min(o.r, dx)); dy = Math.max(-o.t, Math.min(o.b, dy));
            c.l = o.l + dx; c.r = o.r - dx; c.t = o.t + dy; c.b = o.b - dy;
          } else {
            if (c0[1] === 'l') c.l = Math.max(0, Math.min(1 - o.r - MIN, o.l + dx)); else c.r = Math.max(0, Math.min(1 - o.l - MIN, o.r - dx));
            if (c0[0] === 't') c.t = Math.max(0, Math.min(1 - o.b - MIN, o.t + dy)); else c.b = Math.max(0, Math.min(1 - o.t - MIN, o.b - dy));
          }
          paint();
        }
        function up() { box.removeEventListener('pointermove', move); box.removeEventListener('pointerup', up); box.removeEventListener('pointercancel', up); renderPreview(); }
        box.addEventListener('pointermove', move); box.addEventListener('pointerup', up); box.addEventListener('pointercancel', up);
      });
      return box;
    }
    function renderLogo() {
      var lf = logoField();
      ui.logoWrap.hidden = !lf;
      if (!lf) return;
      var L = st.logo;
      ui.logoOpts.innerHTML = '';
      ui.fileLabel.textContent = L ? 'Change file' : 'Upload your logo';
      if (!L || !L.print) { ui.logoNote.className = 'egpz-note'; ui.logoNote.textContent = 'PNG, JPG or SVG.'; return; }
      var e = L.edit, p = logoPlace(lf, L.print, e), low = p.dpi < MIN_DPI;
      ui.logoNote.className = 'egpz-note' + (low ? ' egpz-note--warn' : '');
      ui.logoNote.textContent = (L.name ? L.name + ': ' : '') + (low
        ? 'low resolution at this size (about ' + p.dpi + ' DPI). It may print soft; we’ll email you if we need a sharper file.'
        : 'drag it on the case to move it. Anything past the dotted line is trimmed.');
      var tools = el('div', { class: 'egpz-edit' });
      function slider(key, label, min, max, step, shown, unit) {
        var row = el('label', { class: 'egpz-slider' }, '<span>' + label + '</span><input type="range" min="' + min + '" max="' + max + '" step="' + step + '" value="' + shown + '" data-pz-edit="' + key + '" data-unit="' + unit + '" aria-label="Logo ' + label.toLowerCase() + '"><input class="egpz-val" type="text" readonly tabindex="-1" aria-hidden="true" value="' + shown + unit + '">');
        tools.appendChild(row);
      }
      slider('s', 'Size', 20, 300, 5, Math.round(e.s * 20) * 5, '%');
      slider('r', 'Rotate', -180, 180, 15, e.r, '°');
      var btns = el('div', { class: 'egpz-chips' });
      btns.appendChild(chip({ 'data-pz-logo-act': 'center' }, 'Center', false));
      btns.appendChild(chip({ 'data-pz-logo-act': 'crop' }, 'Crop', !!L.cropping));
      btns.appendChild(chip({ 'data-pz-logo-act': 'reset' }, 'Reset', false));
      tools.appendChild(btns);
      ui.logoOpts.appendChild(tools);
      if (L.cropping) ui.logoOpts.appendChild(cropper(L));
      if (st.ink !== 'color') {
        var inv = el('label', { class: 'egpz-check' }, '<input type="checkbox"' + (L.invert ? ' checked' : '') + '> Print the light parts instead');
        inv.querySelector('input').addEventListener('change', function (e) { L.invert = e.target.checked; reprocess(); renderAll(); });
        ui.logoOpts.appendChild(inv);
      } else if (!L.print.transparent) {
        var ko = el('label', { class: 'egpz-check' }, '<input type="checkbox"' + (L.knockout ? ' checked' : '') + '> Remove the white background');
        ko.querySelector('input').addEventListener('change', function (e) { L.knockout = e.target.checked; reprocess(); renderAll(); });
        ui.logoOpts.appendChild(ko);
      }
      var clr = el('button', { type: 'button', class: 'egpz-link' }, 'Remove');
      clr.addEventListener('click', function () { st.logo = null; ui.file.value = ''; renderAll(); });
      ui.logoOpts.appendChild(clr);
    }
    function renderStyle() {
      ui.fontWrap.hidden = !needsFont();
      ui.fonts.innerHTML = '';
      cfg.fonts.forEach(function (f) {
        loadFont(f).then(function () { if (st.open) { renderLayouts(); renderAccents(); renderPreview(); } });
        ui.fonts.appendChild(chip({ 'data-font': f.id, style: 'font-family:"' + f.family + '",sans-serif' }, esc(f.label), st.font === f.id));
      });
      var inks = inkChoices();
      ui.inkWrap.hidden = inks.length < 2;
      ui.inks.innerHTML = '';
      inks.forEach(function (id) {
        // on logos, one-ink is the cheaper option next to the full-color default
        var k = inkOf(id), extra = kind().tier === 2 && id !== 'color' && fees[2] && fees[3] && st.ink === 'color'
          ? ' <small>−' + esc(money((fees[3].price - fees[2].price) * 100, product.money)) + '</small>'
          : id === 'color' && st.ink !== 'color' && fees[2] && fees[3] ? ' <small>+' + esc(money((fees[3].price - fees[2].price) * 100, product.money)) + '</small>' : '';
        ui.inks.appendChild(chip({ 'data-ink': id }, '<span class="egpz-sw egpz-sw--' + id + '" style="background:' + esc(k.swatch) + '"></span>' + esc(k.label) + extra, st.ink === id));
      });
    }
    function renderAccents() {
      var fit = fittingAccents();
      ui.accentWrap.hidden = !fit.length;
      if (!fit.length) return;
      var open = st.accentOpen || !!st.accent;
      ui.accentToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      ui.accents.hidden = !open;
      ui.accentLabel.hidden = !open;
      ui.accents.innerHTML = '';
      if (!open) return;
      // thumbnails show the current design with the accent in its corner
      var base = layoutFields(tpl());
      ui.accents.appendChild(thumbChip({ 'data-accent': '' }, 'None', !st.accent, base, st.font));
      fit.forEach(function (a) { ui.accents.appendChild(thumbChip({ 'data-accent': a.slug }, a.name, st.accent === a.slug, base.concat(a.fields), st.font)); });
    }
    function renderCases() {
      var tf = textFields();
      ui.casesLabel.textContent = tf.length ? 'Your text' : 'How many';
      ui.cases.innerHTML = '';
      // Nothing to type (logo only): a quantity stepper instead of identical rows.
      if (!tf.length) {
        var n = st.cases.length;
        ui.cases.innerHTML = '<div class="egpz-qty"><button type="button" class="egpz-qty__btn" data-pz-qty="-1" aria-label="One fewer case"' + (n < 2 ? ' disabled' : '') + '>−</button>' +
          '<span class="egpz-qty__n" aria-live="polite">' + n + ' case' + (n > 1 ? 's' : '') + '</span>' +
          '<button type="button" class="egpz-qty__btn" data-pz-qty="1" aria-label="One more case"' + (n >= cfg.max_cases ? ' disabled' : '') + '>+</button></div>';
        ui.add.hidden = true;
        ui.quote.hidden = n < cfg.max_cases;
        return;
      }
      st.cases.forEach(function (c, i) {
        var open = i === st.openIdx;
        var title = tf.length ? (c[tf[0].key] || '').toUpperCase() || 'Add your text' : 'Same logo';
        var row = el('div', { class: 'egpz-case' + (open ? ' is-open' : '') });
        row.innerHTML = '<button type="button" class="egpz-case__head" data-pz-case="' + i + '" aria-expanded="' + open + '">' +
          '<span class="egpz-case__n">' + (i + 1) + '</span><span class="egpz-case__name">' + esc(title) + '</span>' +
          (st.cases.length > 1 ? '<svg class="egpz-case__chev" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 6l5 5 5-5"/></svg>' : '') + '</button>';
        if (open && tf.length) {
          var body = el('div', { class: 'egpz-case__body' });
          tf.forEach(function (f) {
            var id = 'egpz-' + root.id + '-' + i + '-' + f.key;
            var wrap = el('div', { class: 'egpz-field' });
            wrap.innerHTML = '<label for="' + id + '">' + esc(f.label) + (f.required ? '' : ' <em>(optional)</em>') + '</label>' +
              '<input type="text" id="' + id + '" data-pz-i="' + i + '" data-pz-k="' + esc(f.key) + '" maxlength="' + (f.max_chars || 24) + '" value="' + esc(c[f.key] || '') +
              '" placeholder="' + esc(f.placeholder || '') + '" autocomplete="off" autocapitalize="' + (f.uppercase ? 'characters' : 'words') + '" enterkeyhint="done">' +
              '<span class="egpz-count">' + (c[f.key] || '').length + '/' + (f.max_chars || 24) + '</span>';
            body.appendChild(wrap);
          });
          if (st.cases.length > 1) body.appendChild(el('button', { type: 'button', class: 'egpz-link egpz-link--danger', 'data-pz-rm': i }, 'Remove'));
          row.appendChild(body);
        } else if (open && st.cases.length > 1) {
          row.appendChild(el('div', { class: 'egpz-case__body' }, '<button type="button" class="egpz-link egpz-link--danger" data-pz-rm="' + i + '">Remove</button>'));
        }
        ui.cases.appendChild(row);
      });
      var full = st.cases.length >= cfg.max_cases;
      ui.add.hidden = full;
      ui.quote.hidden = !full;
    }
    // Bulk pricing the product already has (Basecamp quantity-break ladder, applied automatically by
    // Shopify): per-variant unit prices in cents, so the summary matches the cart. Stale data (>30 days)
    // is ignored, same rule as the buy-box block. Counts only this batch; the cart may do better.
    function ladderPrices(v) {
      var qb = product.breaks;
      if (!qb || !qb.variants) return null;
      var synced = Date.parse(qb.synced_at || '');
      if (synced && Date.now() - synced > 30 * 864e5) return null;
      var row = qb.variants.find(function (x) { return String(x.id).split('/').pop() === String(v.id); });
      return row && row.prices && row.prices.length ? row.prices.slice().sort(function (a, b) { return a.min - b.min; }) : null;
    }
    function caseUnit(v, n) {
      var unit = v.price;
      (ladderPrices(v) || []).forEach(function (p) { if (n >= p.min) unit = Math.min(unit, p.unit); });
      return unit;
    }
    // "Buy more, save more" next to Add another case: the product's own ladder, this batch highlighted.
    function renderLadder(v, n, next, nextPct) {
      var prices = ladderPrices(v);
      ui.ladder.hidden = !prices || prices.length < 2;
      if (ui.ladder.hidden) return;
      var steps = prices.map(function (p, i) {
        var pct = Math.round((1 - p.unit / v.price) * 100), upto = prices[i + 1] ? prices[i + 1].min - 1 : null;
        var on = n >= p.min && (!prices[i + 1] || n < prices[i + 1].min);
        var qty = upto === p.min ? p.min : upto ? p.min + '–' + upto : p.min + '+';
        return '<li class="egpz-ladder__step' + (on ? ' is-on' : '') + (n >= p.min ? ' is-hit' : '') + '"><b>' + qty + '</b><span>' +
          (pct ? pct + '% off' : 'Full price') + '</span></li>';
      }).join('');
      var msg = next
        ? 'Add ' + (next.min - n) + ' more case' + (next.min - n > 1 ? 's' : '') + ' and every case is ' + nextPct + '% off.'
        : 'You’re getting the best case price.';
      ui.ladder.innerHTML = '<p class="egpz-ladder__title">Buy more, save more <span>applied automatically at checkout</span></p>' +
        '<ol class="egpz-ladder__steps">' + steps + '</ol><p class="egpz-ladder__msg">' + esc(msg) + '</p>';
    }
    function renderSum() {
      var n = st.cases.length, t = fees[tier()], v = currentVariant(), c = color();
      ui.unavail.hidden = !!c;
      if (!c) ui.unavail.textContent = 'Personalization isn’t available in ' + v.options[colorOpt] + ' yet. Pick another case color above.';
      var unit = caseUnit(v, n), save = (v.price - unit) * n;
      var next = (ladderPrices(v) || []).find(function (p) { return p.min > n && p.unit < unit; });
      var nextPct = next ? Math.round((1 - next.unit / v.price) * 100) : 0;
      ui.sum.innerHTML =
        '<div><span>' + n + ' × ' + esc(cfg.name) + (c ? ' (' + esc(c.name) + ')' : '') + '</span><span>' + esc(money(v.price * n, product.money)) + '</span></div>' +
        (save > 0 ? '<div class="egpz-sum__save"><span>Bulk pricing, ' + n + ' cases</span><span>−' + esc(money(save, product.money)) + '</span></div>' : '') +
        '<div><span>' + esc(t.label) + ' print × ' + n + '</span><span>+' + esc(money(t.price * 100 * n, product.money)) + '</span></div>' +
        '<div class="egpz-sum__tot"><span>Total</span><span>' + esc(money((unit + t.price * 100) * n, product.money)) + '</span></div>';
      st.totalText = money((unit + t.price * 100) * n, product.money);
      renderLadder(v, n, next, nextPct);
      ui.cta.textContent = st.busy ? 'Adding…' : 'Add ' + (n > 1 ? n + ' cases' : 'to cart');
      ui.cta.disabled = st.busy || !c || !v.available;
      ui.err.textContent = st.error;
      ui.err.hidden = !st.error;
      syncSticky();
    }

    // ── sticky Add to Cart bar (sections/product.liquid #StickyATC) ──
    // While personalizing, the bar shows the personalized total and a badge, and its button runs the
    // personalizer's own Add (designs saved + fee line), never the plain theme add. It steps aside
    // while the personalizer's button is on screen, and goes back to normal when the panel closes.
    var sticky = document.getElementById('StickyATC');
    var stickyBtn = document.getElementById('StickyATCBtn');
    var stickyPrice = sticky && sticky.querySelector('.sticky-atc__price');
    // what the theme itself would show (kept current from its own events), restored on close
    var themePrice = stickyPrice ? stickyPrice.textContent : '';
    var stickyOn = false;
    function syncSticky() {
      if (!sticky || !stickyBtn || !stickyPrice) return;
      var on = st.open;
      sticky.classList.toggle('egpz-sticky', on);
      var badge = sticky.querySelector('.egpz-sticky__badge');
      if (on) {
        stickyOn = true;
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'egpz-sticky__badge';
          badge.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg><span></span>';
          stickyPrice.parentNode.insertBefore(badge, stickyPrice);
        }
        var n = st.cases.length;
        badge.lastChild.textContent = 'Personalized' + (n > 1 ? ' · ' + n + ' cases' : '');
        if (st.totalText && stickyPrice.textContent !== st.totalText) {
          stickyPrice.textContent = st.totalText;
          stickyPrice.classList.remove('eg-flip'); void stickyPrice.offsetWidth; stickyPrice.classList.add('eg-flip');
        }
        stickyBtn.textContent = ui.cta.textContent;
        stickyBtn.disabled = ui.cta.disabled;
      } else if (stickyOn) {
        stickyOn = false;
        if (badge) badge.remove();
        var v = currentVariant();
        stickyPrice.textContent = themePrice;
        stickyBtn.textContent = v.available ? 'Add to Cart' : 'Sold Out';
        stickyBtn.disabled = !v.available;
      }
    }
    if (sticky && stickyBtn) {
      // capture phase: runs before the theme's own click handler, which would add a plain case
      stickyBtn.addEventListener('click', function (e) {
        if (!st.open) return;
        e.preventDefault(); e.stopImmediatePropagation();
        if (ui.cta.disabled) { ui.cta.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
        ui.cta.click();
        setTimeout(function () { if (st.error) ui.err.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 1500);
      }, true);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          sticky.classList.toggle('egpz-cta-in-view', entries[0].isIntersecting);
        }).observe(ui.cta);
      }
      // The theme rewrites the bar on variant / bulk-tier changes: remember its value for later, then
      // put the personalized view back while personalizing.
      document.addEventListener('eg:qb:price', function (e) {
        if (e.detail && e.detail.text) themePrice = e.detail.text;
        if (st.open) setTimeout(renderSum, 0);
      });
      document.addEventListener('variant:change', function (e) {
        var v = e.detail && e.detail.variant;
        if (v && v.price != null) themePrice = (v.price / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
        if (st.open) setTimeout(renderSum, 0);
      });
    }

    function renderAll() {
      syncDefaults();
      renderKinds(); renderLayouts(); renderLogo(); renderCases(); renderStyle(); renderAccents(); renderPreview(); renderSum();
      renderTeaser();
    }

    // ── closed card: the shopper's case color with a sample name on the lid, cycling gently ──
    var textKind = KINDS.find(function (k) { return k.tier === 1; });
    // the one-field text layout with the biggest lettering reads best at card size
    function bigText(t) { var tf = t.fields.filter(function (f) { return f.kind === 'text'; }); return tf.length === 1 && !tf[0].rot ? tf[0].h_mm * Math.min(tf[0].w_mm, 120) : 0; }
    var teaserTpl = textKind && textKind.tpls.slice().sort(function (a, b) { return bigText(b) - bigText(a); })[0];
    var teaserKey = teaserTpl && (teaserTpl.fields.find(function (f) { return f.kind === 'text'; }) || {}).key;
    var SAMPLES = ['THE CARTERS', 'EST. 2024', 'J.M.R', 'BASECAMP 01'];
    var sampleAt = 0;
    function renderTeaser() {
      if (!ui.tphoto) return;
      var c = color() || shown;
      if (c && ui.tphoto.getAttribute('data-src') !== c.photo_url) {
        ui.tphoto.src = c.photo_url + (c.photo_url.indexOf('?') > -1 ? '&' : '?') + 'width=600';
        ui.tphoto.setAttribute('data-src', c.photo_url);
        var b = c.box, s = ui.tsvg.style;
        s.left = b.x * 100 + '%'; s.top = b.y * 100 + '%'; s.width = b.w * 100 + '%'; s.height = b.h * 100 + '%';
        // zoom the square photo so the whole case sits in the card's 1.45:1 tile with an even margin
        // (the case body is ~1.16× the lid frame wide)
        var AR = 1.45, k = Math.max(1, 0.8 / (b.w * 1.16));
        ui.tzoom.style.transform = 'translate(' + (0.5 - (b.x + b.w / 2) * k) * 100 + '%,' + (0.5 / AR - (b.y + b.h / 2) * k) * 100 + '%) scale(' + k + ')';
      }
      if (!teaserTpl || !c) return;
      var vals = {}; if (teaserKey) vals[teaserKey] = SAMPLES[sampleAt % SAMPLES.length];
      var keep = st.ink; st.ink = (c.inks || [])[0] || keep;
      drawPreview(ui.tsvg, c, teaserTpl.fields, vals, { id: 'teaser', font: teaserTpl.font, clean: true });
      st.ink = keep;
    }
    if (!window.matchMedia || !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setInterval(function () {
        if (st.open || document.hidden || !ui.tsvg) return;
        ui.tsvg.classList.add('is-fading');
        setTimeout(function () { sampleAt++; renderTeaser(); ui.tsvg.classList.remove('is-fading'); }, 260);
      }, 2800);
    }

    function reprocess() {
      var L = st.logo;
      if (!L || !L.img) return;
      L.print = processLogo(L.img, st.ink === 'color' ? 'color' : 'mono', { invert: L.invert, knockout: L.knockout, name: L.name });
    }

    // ── events ──
    ui.toggle.addEventListener('click', function () {
      st.open = !st.open;
      ui.toggle.setAttribute('aria-expanded', st.open);
      root.classList.toggle('is-open', st.open);
      ui.panel.hidden = !st.open;
      if (ui.tcta) ui.tcta.textContent = st.open ? 'Close' : 'Personalize';
      if (st.open) {
        renderAll();
        var first = ui.cases.querySelector('.is-open input');
        if (first && window.innerWidth > 749) first.focus({ preventScroll: true });
      } else syncSticky();
    });
    ui.svg.addEventListener('pointerdown', function (e) {
      var L = st.logo, lf = logoField();
      if (!e.target.hasAttribute('data-pz-grab') || !L || !lf) return;
      e.preventDefault(); ui.svg.setPointerCapture(e.pointerId);
      var mm = cfg.zone.w_mm / ui.svg.getBoundingClientRect().width, sx = e.clientX, sy = e.clientY, ox = L.edit.x, oy = L.edit.y;
      var t = -(lf.rot || 0) * Math.PI / 180, cos = Math.cos(t), sin = Math.sin(t); // screen → the field's own frame
      function move(ev) {
        var dx = (ev.clientX - sx) * mm, dy = (ev.clientY - sy) * mm;
        L.edit.x = ox + dx * cos - dy * sin; L.edit.y = oy + dx * sin + dy * cos;
        clampEdit(); queuePlace();
      }
      function up() { ui.svg.removeEventListener('pointermove', move); ui.svg.removeEventListener('pointerup', up); ui.svg.removeEventListener('pointercancel', up); }
      ui.svg.addEventListener('pointermove', move); ui.svg.addEventListener('pointerup', up); ui.svg.addEventListener('pointercancel', up);
    });
    root.addEventListener('input', function (e) {
      var key = e.target.getAttribute && e.target.getAttribute('data-pz-edit');
      if (!key || !st.logo) return;
      if (key === 's') st.logo.edit.s = +e.target.value / 100;
      else st.logo.edit.r = +e.target.value;
      // a field's value, not page text: store scripts watch the page and text changes made the slider lag
      e.target.nextElementSibling.value = e.target.value + e.target.getAttribute('data-unit');
      queuePlace();
    });
    root.addEventListener('change', function (e) {
      if (!e.target.getAttribute) return;
      if (e.target.getAttribute('data-pz-edit')) { renderPreview(); renderLogo(); }
      if (e.target.hasAttribute('data-pz-approve')) { st.approved = e.target.checked; if (st.approved) { st.error = ''; renderSum(); } }
    });
    // Going to the full editor takes the work along: text, layout and edits in sessionStorage, the logo file in IndexedDB.
    function handoff(url) {
      var L = st.logo, go = function () { window.location.href = url; };
      try {
        sessionStorage.setItem('egpz-handoff', JSON.stringify({
          tpl: st.tpl, font: st.font, ink: st.ink, flip: st.flip, variant: product.current, cases: st.cases,
          logo: !!L, name: L && L.name, invert: L && L.invert, knockout: L && L.knockout, edit: L && L.edit
        }));
      } catch (e) { return go(); }
      if (!L || !L.file) return go();
      setTimeout(go, 1500);
      pzDb().then(function (db) {
        var tx = db.transaction('f', 'readwrite');
        tx.objectStore('f').put(L.file, 'logo');
        tx.oncomplete = go; tx.onerror = go;
      }).catch(go);
    }
    root.addEventListener('click', function (e) {
      var a = e.target.closest('[data-pz-handoff]');
      if (a && st.open) { e.preventDefault(); handoff(a.getAttribute('href')); }
    });
    root.addEventListener('click', function (e) {
      var b = e.target.closest('button');
      if (!b || !root.contains(b)) return;
      if (b.hasAttribute('data-kind')) {
        var was = kind().tier;
        st.kind = b.getAttribute('data-kind'); st.error = '';
        // logos start in full color (one-ink stays a click away); text can't be full color
        if (kind().tier === 2 && was !== 2 && fees[3]) { st.ink = 'color'; reprocess(); }
        renderAll();
      }
      else if (b.hasAttribute('data-tpl')) { st.tpl = b.getAttribute('data-tpl'); if (tpl().font) st.font = tpl().font; resetPlace(); renderAll(); }
      else if (b.hasAttribute('data-pz-qty')) {
        var d = +b.getAttribute('data-pz-qty');
        if (d > 0 && st.cases.length < cfg.max_cases) st.cases.push({});
        if (d < 0 && st.cases.length > 1) st.cases.pop();
        st.openIdx = Math.min(st.openIdx, st.cases.length - 1);
        renderCases(); renderPreview(); renderSum();
      }
      else if (b.hasAttribute('data-pz-logo-act')) {
        var act = b.getAttribute('data-pz-logo-act'), L = st.logo;
        if (act === 'center') { L.edit.x = 0; L.edit.y = 0; }
        else if (act === 'reset') { L.edit = freshEdit(); L.cropping = false; }
        else L.cropping = !L.cropping;
        renderAll();
      }
      else if (b.hasAttribute('data-pz-flip')) { st.flip = !st.flip; resetPlace(); renderAll(); }
      else if (b.hasAttribute('data-pz-accent-toggle')) { st.accentOpen = !(st.accentOpen || st.accent); if (!st.accentOpen) st.accent = null; renderAll(); }
      else if (b.hasAttribute('data-accent')) { st.accent = b.getAttribute('data-accent') || null; renderAll(); }
      else if (b.hasAttribute('data-font')) { st.font = b.getAttribute('data-font'); renderStyle(); renderLayouts(); renderAccents(); renderPreview(); }
      else if (b.hasAttribute('data-ink')) {
        var was = st.ink; st.ink = b.getAttribute('data-ink');
        if ((was === 'color') !== (st.ink === 'color')) reprocess();
        renderAll();
      } else if (b.hasAttribute('data-pz-case')) {
        st.openIdx = +b.getAttribute('data-pz-case');
        renderCases(); renderPreview();
        var inp = ui.cases.querySelector('.is-open input'); if (inp) inp.focus({ preventScroll: true });
      } else if (b.hasAttribute('data-pz-rm')) {
        st.cases.splice(+b.getAttribute('data-pz-rm'), 1);
        st.openIdx = Math.min(st.openIdx, st.cases.length - 1);
        renderCases(); renderPreview(); renderSum();
      }
    });
    ui.cases.addEventListener('input', function (e) {
      var t = e.target;
      if (!t.hasAttribute('data-pz-k')) return;
      var i = +t.getAttribute('data-pz-i'), k = t.getAttribute('data-pz-k');
      var f = textFields().find(function (x) { return x.key === k; });
      if (f && f.uppercase) { var pos = t.selectionStart; t.value = t.value.toUpperCase(); t.setSelectionRange(pos, pos); }
      st.cases[i][k] = t.value;
      var cnt = t.parentNode.querySelector('.egpz-count');
      if (cnt) cnt.textContent = t.value.length + '/' + (f && f.max_chars || 24);
      if (textFields()[0] && textFields()[0].key === k) {
        t.closest('.egpz-case').querySelector('.egpz-case__name').textContent = t.value.toUpperCase() || 'Add your text';
      }
      st.error = '';
      renderPreview(); ui.err.hidden = true;
    });
    ui.add.addEventListener('click', function () {
      // New row keeps the shared lines (dates, roles, accents), clears the name.
      var prev = st.cases[st.cases.length - 1] || {}, next = {};
      textFields().forEach(function (f, idx) { if (idx > 0 && prev[f.key]) next[f.key] = prev[f.key]; });
      st.cases.push(next);
      st.openIdx = st.cases.length - 1;
      renderCases(); renderPreview(); renderSum();
      var inp = ui.cases.querySelector('.is-open input'); if (inp) inp.focus();
    });
    ui.file.addEventListener('change', function () {
      var f = ui.file.files && ui.file.files[0];
      if (!f) return;
      if (f.size > 15e6) { st.error = 'That file is over 15 MB. Please use a smaller one.'; renderSum(); return; }
      readImage(f).then(function (img) {
        st.logo = { img: img, name: f.name.slice(0, 80), file: f, invert: false, knockout: false, size: 'fill', edit: freshEdit() };
        reprocess();
        if (!st.logo.print) { st.logo = null; st.error = 'We couldn’t find a logo in that image. Try a file with a clear or white background.'; }
        else st.error = '';
        renderAll();
      }).catch(function () { st.error = 'We couldn’t read that file. Please use a PNG, JPG or SVG.'; renderSum(); });
    });

    // ── add to cart ──
    function validate() {
      var tf = textFields();
      for (var i = 0; i < st.cases.length; i++) {
        for (var j = 0; j < tf.length; j++) {
          var f = tf[j], v = (st.cases[i][f.key] || '').trim();
          if (f.required && !v) return { i: i, msg: (st.cases.length > 1 ? 'Case ' + (i + 1) + ': add ' : 'Add ') + f.label.toLowerCase() + '.' };
          if (v && !/^[A-Za-z0-9 .,'&\-#!?/:+()·"‘’“”–—]*$/.test(v)) return { i: i, msg: f.label + ' can use letters, numbers and simple punctuation.' };
        }
      }
      if (logoField() && !(st.logo && st.logo.print)) return { i: -1, msg: 'Upload your logo first.' };
      if (!st.approved) return { i: -1, msg: 'Check the box to approve your proof.', approve: true };
      return null;
    }
    ui.cta.addEventListener('click', function () {
      if (st.busy) return;
      var bad = validate();
      if (bad) {
        st.error = bad.msg;
        if (bad.approve) root.querySelector('[data-pz-approve-box]').scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (bad.i >= 0) { st.openIdx = bad.i; renderCases(); renderPreview(); var inp = ui.cases.querySelector('.is-open input'); if (inp) inp.focus(); }
        renderSum();
        return;
      }
      var t = tpl(), fee = fees[tier()], v = currentVariant(), c = color();
      if (bcPreview) { ui.err.classList.add('is-info'); st.error = 'Preview: on the store this adds ' + st.cases.length + ' personalized case' + (st.cases.length > 1 ? 's' : '') + ' (' + st.totalText + ') to the cart.'; renderSum(); return; }
      if (!fee.fee_variant_id) { st.error = 'Personalization is being set up. Please try again later.'; renderSum(); return; }
      var flipped = !!(st.flip && isFlippable(t.fields));
      st.busy = true; st.error = ''; renderSum();
      var lf = logoField();
      Promise.resolve(lf ? bakedLogo(st.logo.print, lf, st.logo.edit) : null).then(function (logo) {
        var payload = {
          action: 'save', profile: cfg.profile, template: t.slug, ink: ink(), font: needsFont() ? st.font : null,
          accent: st.accent, flip: flipped,
          color: c.name, product_handle: product.handle, variant_id: String(v.id), variant_title: v.options.join(' / '),
          logo: logo ? { data_url: logo.dataUrl, name: st.logo.name, size: st.logo.size } : null,
          cases: st.cases.map(function (cs) {
            var vals = {}; textFields().forEach(function (f) { if (cs[f.key]) vals[f.key] = cs[f.key]; }); return { values: vals };
          })
        };
        return timedFetch(cfg.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      })
        .then(function (r) { return r.json().then(function (b) { if (!r.ok) throw new Error(b && b.error || 'save'); return b; }); })
        .then(function (saved) {
          var f = font(), k = inkOf(ink());
          var items = saved.designs.map(function (d, i) {
            var props = { Personalization: fee.label, Layout: t.name + (flipped ? ' (flipped)' : '') };
            textFields().forEach(function (fld) { var val = st.cases[i][fld.key]; if (val) props[fld.label] = fld.uppercase ? val.toUpperCase() : val; });
            if (logoField()) props.Logo = st.logo.name || 'Uploaded';
            if (needsFont() && f) props.Font = f.label;
            props.Print = k.label;
            props._pz_design = d.id;
            props._pz_group = saved.group_id;
            return { id: v.id, quantity: 1, properties: props };
          });
          items.push({ id: +fee.fee_variant_id, quantity: items.length, properties: { For: items.length + ' personalized ' + (items.length > 1 ? 'cases' : 'case') + ' (' + cfg.name + ')', _pz_group: saved.group_id } });
          window.__egbbAdding = true;
          return timedFetch((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items })
          }).then(function (r) { return r.json().then(function (b) { if (!r.ok) throw new Error(b && (b.description || b.message) || 'cart'); return b; }); });
        })
        .then(function () { window.location.href = (window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart'; })
        .catch(function (e) {
          st.busy = false;
          st.error = e && e.message && e.message !== 'save' && e.message !== 'cart' ? e.message : 'Something went wrong. Please try again.';
          renderSum();
        });
    });

    // Follow the product form's color picker: it decides the case color here.
    document.addEventListener('change', function (e) {
      var form = e.target.closest && e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      var idEl = form.querySelector('[name="id"]');
      var vid = idEl && +idEl.value;
      if (!product.variants.some(function (x) { return x.id === vid; })) return;
      product.current = vid;
      if (st.open) renderAll(); else renderTeaser();
    });

    syncDefaults();
    renderTeaser();
    if (location.hash === '#personalize') { ui.toggle.click(); }
  }

  // One block per page: the boot snippet (any product template) and the older section can both be
  // on a page, and this file can load twice; the first data block wins, the rest are removed.
  function boot() {
    if (window.__egpzBooted) return;
    var roots = [].slice.call(document.querySelectorAll('[data-eg-pz]'));
    if (!roots.length) return;
    window.__egpzBooted = true;
    roots.slice(1).forEach(function (r) { r.remove(); });
    init(roots[0]);
  }
  window.EGPZ_BOOT = boot;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
